import PropTypes from 'prop-types';
import React, { useId, useRef, useState } from 'react';
import { Modal, ModalBody, Spinner } from 'reactstrap';

const DeleteModal = ({
  show,
  onDeleteClick,
  onCloseClick,
  loading = false,
  title = 'Delete this record?',
  message = 'Are you sure you want to remove this record? This cannot be undone.',
  subject,
  confirmLabel = 'Yes, delete it',
}) => {
  // An async onDeleteClick keeps the button busy until it settles and drops any
  // click that arrives meanwhile, so callers do not need a flag of their own.
  const [pending, setPending] = useState(false);
  const pendingRef = useRef(false);
  const busy = loading || pending;
  const titleId = `hy-delete-title-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

  const handleDelete = e => {
    if (pendingRef.current) return;
    const result = onDeleteClick?.(e);
    if (typeof result?.then === 'function') {
      pendingRef.current = true;
      setPending(true);
      const settle = () => {
        pendingRef.current = false;
        setPending(false);
      };
      result.then(settle, settle);
    }
  };

  return (
    <Modal
      fade={true}
      isOpen={show}
      toggle={onCloseClick}
      centered={true}
      role='alertdialog'
      labelledBy={titleId}
    >
      <ModalBody className='p-4 p-sm-5 pb-sm-4'>
        <div className='text-center'>
          <div className='hy-confirm-icon mb-4' aria-hidden='true'>
            <i className='ri-delete-bin-6-line' aria-hidden='true'></i>
          </div>
          <h4 className='fs-18 mb-2' id={titleId}>
            {title}
          </h4>
          <p className='text-muted mb-0'>{message}</p>
          {subject && <div className='hy-confirm-subject'>{subject}</div>}
        </div>
        <div className='d-flex flex-wrap gap-2 justify-content-center mt-4'>
          <button
            type='button'
            className='btn w-sm btn-light'
            data-bs-dismiss='modal'
            onClick={onCloseClick}
          >
            <i className='ri-close-line align-middle me-2' aria-hidden='true'></i>
            Cancel
          </button>
          <button
            type='button'
            className='btn w-sm btn-danger'
            id='delete-record'
            disabled={busy}
            aria-busy={busy || undefined}
            onClick={handleDelete}
          >
            {busy ? (
              <Spinner size='sm' className='me-2'>
                Deleting…
              </Spinner>
            ) : (
              <i
                className='ri-delete-bin-line align-middle me-2'
                aria-hidden='true'
              ></i>
            )}
            {confirmLabel}
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
};

DeleteModal.propTypes = {
  onCloseClick: PropTypes.func,
  onDeleteClick: PropTypes.func,
  show: PropTypes.any,
  loading: PropTypes.bool,
  title: PropTypes.string,
  message: PropTypes.node,
  subject: PropTypes.node,
  confirmLabel: PropTypes.string,
};

export default DeleteModal;
